/*
 * Copyright © 2020-2025 XCamera. All Rights Reserved.
 * @Description: AI识别模块统一埋点Hook
 * @LastEditors: yingongjing
 * @LastEditTime: 2025-09-03 20:51:33
 */
import { useCallback, useRef, useEffect } from 'react'
import sensors from '../../../utils/sensors'
import { useAISecurity } from '../context/AISecurityContext'
import { XC_RECOGNITION_STATUS } from '../types/contextInterface'
import type {
  RecognitionRecord,
  XCRecognitionResult
} from '../types/contextInterface'
import CONFIG from '../../../constants/config'
import { getTabName } from '../lib/columnConfigs'

/**
 * 埋点事件类型定义
 */
export interface XCSensorTrackingParams {
  actionType: string
  picUrl?: string
  [key: string]: any
}

/**
 * 生成基础埋点参数
 * @param record - 记录数据
 * @param colIndex - 列索引（可选，第几列，从0开始）
 * @param getTabName - 获取列名称的函数
 * @returns 基础埋点参数对象
 */
export const generateBaseTrackingParams = (
  record: RecognitionRecord,
  colIndex?: number
) => {
  const baseParams = {
    picUrl: record.url,
    nickname: record.nickName,
    datetime1: record.createTime,
    taskID: record.id,
    string1: record.potentialHazardList?.join('\n'),
    string2: record.standardList?.join('\n'),
    string3: record.thinking,
    string4: record.RectificationMeasure,
    string5: record.uuid,
    string6: record.monomerName,
    string7: record.questionType,
    string8: record.RectificationManager,
    string9: record.ConstructioUnit,
    string10: record.projectName,
    datetime2: record.deadline,
    datetime8: record.finishTime,
    remarkText: record.comment,
    imageUrl: record.photoFilesAfter?.join(',')
  }

  // 如果提供了列索引和getTabName函数，添加列相关信息
  if (typeof colIndex === 'number') {
    const columnInfo = getTabName(colIndex)
    return {
      ...baseParams,
      tabName: columnInfo.title
    }
  }

  return baseParams
}

/**
 * AI识别模块统一埋点Hook
 * 提供统一的埋点上报功能，避免重复代码
 */
export const useSensorTracking = () => {
  const {
    userId,
    groupId,
    isGroupVIP,
    myRole,
    recognitionResult,
    selectedImages,
    recognitionStatus,
    rememberChoice
  } = useAISecurity()

  // 使用 useRef 保存最新的状态值
  const latestStateRef = useRef({
    recognitionResult,
    selectedImages,
    recognitionStatus,
    rememberChoice
  })

  // 更新 ref 中的最新值
  useEffect(() => {
    latestStateRef.current = {
      recognitionResult,
      selectedImages,
      recognitionStatus,
      rememberChoice
    }
  }, [recognitionResult, selectedImages, recognitionStatus, rememberChoice])

  // 基础参数，用于所有埋点事件
  const baseParams = {
    // userID: userId,
    groupID: groupId,
    isGroupVIP: isGroupVIP,
    role: CONFIG.saKeywords.roles[myRole]
  }

  /**
   * 获取当前识别相关参数
   * @returns 识别相关参数对象
   */
  const getCurrentRecognitionParams = useCallback((actionType?: string) => {
    // 从 ref 获取最新的状态值
    const {
      recognitionResult: currentResult,
      selectedImages: currentImages,
      recognitionStatus: currentStatus
    } = latestStateRef.current

    // 对于 editContent 事件，不返回识别参数，避免覆盖 generateBaseTrackingParams 的字段
    if (actionType === 'editContent') {
      return {}
    }

    // 动态计算识别相关参数
    return currentStatus === XC_RECOGNITION_STATUS.COMPLETED
      ? {
          string1: currentResult?.potentialHazardList?.join(';') || '',
          string2: currentResult?.standardList?.join(';') || '',
          string3: currentResult?.thinking || '',
          picUrl: currentImages?.[currentImages.length - 1] || undefined
        }
      : {}
  }, [])

  /**
   * web端AI识别安全隐患的过程和结果事件
   * @param params 埋点参数
   * - actionType: 操作类型 indentifyBegin:识别开始   indentifySucc:识别成功 indentifyFail:识别失败
   * - string1: '', // 识别到的安全隐患
   * - string2: '', // 参考标准
   * - string3: '', // 思考过程
   * - picUrl: '', // 图片URL，在识别成功状态时需要上报图片的URL
   * - errorMsg: '', // 错误信息
   * - taskID: '', // 任务ID
   * clickItem 可选值:
   */
  const reportAISafetyHazardsResultClick = useCallback(
    (params: XCSensorTrackingParams, response?: XCRecognitionResult) => {
      // 从 ref 获取最新的状态值
      const {
        recognitionResult: currentResult,
        selectedImages: currentImages
      } = latestStateRef.current

      // 根据 actionType 决定是否上报识别结果参数
      // 识别开始时不上报 string1、string2、string3
      // 识别成功和失败时上报这些参数
      const buildRecognitionParams = () => {
        const baseRecognitionParams = {
          picUrl: currentImages?.[currentImages.length - 1] || undefined
        }

        const shouldIncludeResultData =
          params.actionType === 'indentifySucc' ||
          params.actionType === 'indentifyFail'

        if (shouldIncludeResultData) {
          return {
            ...baseRecognitionParams,
            string1:
              response?.potentialHazardList?.join(';') ||
              currentResult?.potentialHazardList?.join(';') ||
              '',
            string2:
              response?.standardList?.join(';') ||
              currentResult?.standardList?.join(';') ||
              '',
            string3: response?.thinking || currentResult?.thinking || '',
            string4:
              response?.RectificationMeasure ||
              currentResult?.RectificationMeasure ||
              '',
            string5: response?.uuid || currentResult?.uuid || '',
            string6: response?.monomerName || currentResult?.monomerName || '',
            string7:
              response?.questionType || currentResult?.questionType || '',
            string8:
              response?.RectificationManager ||
              currentResult?.RectificationManager ||
              '',
            string9:
              response?.ConstructioUnit || currentResult?.ConstructioUnit || ''
          }
        }

        return baseRecognitionParams
      }

      const recognitionParams = buildRecognitionParams()

      sensors.track('web_get_action_AI_Identify_safety_hazards_result', {
        ...baseParams,
        ...recognitionParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole]
  )

  /**
   * AI安全隐患台账表页面点击事件
   * @param params 埋点参数
   * - string1: '', // 识别到的安全隐患
   * - string2: '', // 参考标准
   * - string3: '', // 思考过程
   * - picUrl: '', // 图片URL，在识别成功状态相关的事件需要上报图片的URL
   * actionType 可选值:
   * - enterPage: 进入页面
   * - clickIdentify: 点击右上角SecurityEntryV2开始识别
   * - uploadPhotoIdentify: 点击上传照片开始识别 固定行隐患照片列 hover 上传照片
   * - choosePhotoIdentify: 点击相册选择图片识别 固定行隐患照片列 hover 相册选图
   * - clickOnePhoto: 点击一张照片放大 点击隐患照片列图片
   * - clickCropPhotoIdentify: 在放大后的AIImageViewer组件图片上框选后点击识别
   * - choosePhotoDate: 筛选时间 参数改变
   * - choosePhotoMember: 筛选成员 点击确定回调
   * - searchMonomerName: 搜索单体名称 输入结束
   * - searchProblemType: 搜索问题类型 选择
   * - searchKeywords: 搜索跟踪关键词 输入结束
   * - beginSearch: 点击开始筛选
   * - reset: 点击重置
   * - downloadExcel: 点击导出表格
   * - editContent: 点击编辑内容
   */
  const reportAISecurityPageActionClick = useCallback(
    (params: XCSensorTrackingParams) => {
      const recognitionParams = getCurrentRecognitionParams(params.actionType)

      // 确保 params 中的字段不会被 recognitionParams 覆盖
      // 特别是 editContent 事件中通过 generateBaseTrackingParams 传入的字段
      sensors.track('web_get_action_ai_security_page', {
        ...baseParams,
        ...recognitionParams,
        ...params // params 应该在最后，确保不被覆盖
      })
    },
    [userId, groupId, isGroupVIP, myRole, getCurrentRecognitionParams]
  )

  /**
   * 上报AI识别安全隐患记录页事件
   * @param params 埋点参数
   * - clickItem: 点击的元素
   * - historyRecordNum: 历史记录总条数
   * - isGroupVIP: 是否团队会员
   * - role: 操作人角色
   * - groupID: 团队ID
   * clickItem 可选值:
   * - enterPage: 进入页面
   * - downloadExcel: 下载表格Excel
   */
  const reportAISafetyHazardsHistoryClick = useCallback(
    (params: XCSensorTrackingParams) => {
      const recognitionParams = getCurrentRecognitionParams(params.actionType)

      sensors.track('web_click_page_AI_Identify_safety_hazards_history', {
        ...baseParams,
        ...recognitionParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole, getCurrentRecognitionParams]
  )

  /**
   * 上报AI安全隐患识别功能满意度反馈事件
   * @param params 埋点参数
   * - score: 评分 (0-10)
   * - content: 反馈内容
   * - taskID: 任务ID
   */
  const reportAISecurityFeedback = useCallback(
    (params: {
      score: number
      content: string
      willPhoneCallback?: boolean
      record?: RecognitionRecord
      fieldName?: string
    }) => {
      const trackingParams = params.record
        ? generateBaseTrackingParams(params.record, undefined)
        : {}

      sensors.track('web_feedback_AI_Identify_safety_hazards_satisfaction', {
        ...baseParams,
        score: params.score,
        content: params.content,
        bool1: params.willPhoneCallback ?? false,
        from: 'aiSecurity',
        tabName: params.fieldName,
        ...trackingParams
      })
    },
    [userId, groupId, isGroupVIP, myRole]
  )

  /**
   * AI安全隐患台账表页点击筛选行为埋点
   * @param params 埋点参数
   * - startTime: 开始时间
   * - endTime: 结束时间
   * - memberNum: 选择的成员数
   * - isAllMember: 是否全员
   * - infoJson: 单体名称+问题类型+隐患关键词
   * - actionType: 行为类型 (searchBegin: 开始筛选, searchResult: 筛选结果出现)
   * - resultNum: 搜索结果数量
   */
  const reportAISecurityClickFilter = useCallback(
    (params: {
      startTime?: string
      endTime?: string
      memberNum?: number
      isAllMember?: boolean
      infoJson?: string
      actionType: string // 'searchBegin' | 'searchResult'
      resultNum?: number
    }) => {
      sensors.track('web_get_action_ai_security_click_filter', {
        ...baseParams,
        ...params
      })
    },
    [userId, groupId, isGroupVIP, myRole]
  )

  return {
    reportAISecurityPageActionClick,
    reportAISafetyHazardsResultClick,
    reportAISafetyHazardsHistoryClick,
    reportAISecurityFeedback,
    reportAISecurityClickFilter
  }
}

export default useSensorTracking
